import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Paper,
  Button,
  Alert
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import StepWizard from '../../components/common/StepWizard';
import { useAppContext } from '../../context/AppContext';

const Step1BasicInfo = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Basic Information
    </Typography>
    <TextField
      fullWidth
      label="MCP Service Name"
      value={data.name || ''}
      onChange={(e) => onDataChange({ name: e.target.value })}
      margin="normal"
      required
    />
    <TextField
      fullWidth
      label="Service Description"
      value={data.description || ''}
      onChange={(e) => onDataChange({ description: e.target.value })}
      margin="normal"
      multiline
      rows={4}
      placeholder="Describe the purpose of this MCP service, e.g., for AI Agent bulk purchasing..."
    />
  </Box>
);

const Step2Products = ({ data, onDataChange }) => {
  const { productCategories } = useAppContext();
  const selectedCategories = data.selectedCategories || [];

  const handleToggle = (categoryId) => {
    const currentIndex = selectedCategories.indexOf(categoryId);
    const newSelected = [...selectedCategories];

    if (currentIndex === -1) {
      newSelected.push(categoryId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onDataChange({ selectedCategories: newSelected });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Product Categories
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Select product categories to provide through MCP service
      </Typography>
      <FormGroup sx={{ mt: 2 }}>
        {productCategories.map((category) => (
          <FormControlLabel
            key={category.id}
            control={
              <Checkbox
                checked={selectedCategories.indexOf(category.id) !== -1}
                onChange={() => handleToggle(category.id)}
              />
            }
            label={`${category.name} (${category.productCount} products)`}
          />
        ))}
      </FormGroup>
    </Box>
  );
};

const Step3BulkSettings = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Bulk Purchase Configuration
    </Typography>
    <TextField
      fullWidth
      label="Maximum Bulk Quantity"
      value={data.maxBulkQuantity || ''}
      onChange={(e) => onDataChange({ maxBulkQuantity: e.target.value })}
      margin="normal"
      type="number"
      placeholder="e.g., 100"
      helperText="Maximum number of products per bulk purchase"
    />
    <TextField
      fullWidth
      label="Bulk Discount Rate (%)"
      value={data.bulkDiscount || ''}
      onChange={(e) => onDataChange({ bulkDiscount: e.target.value })}
      margin="normal"
      type="number"
      placeholder="e.g., 10"
      helperText="Discount percentage for bulk purchases"
    />
    <TextField
      fullWidth
      label="API Endpoint"
      value={data.apiEndpoint || ''}
      onChange={(e) => onDataChange({ apiEndpoint: e.target.value })}
      margin="normal"
      placeholder="https://api.example.com/mcp"
      helperText="API access address for MCP service"
    />
  </Box>
);

const Step4ShareConfig = ({ data }) => {
  const { productCategories } = useAppContext();
  const selectedCategories = data.selectedCategories || [];
  const selectedCategoryNames = productCategories
    .filter(cat => selectedCategories.includes(cat.id))
    .map(cat => cat.name);

  const configJson = JSON.stringify({
    name: data.name || 'MCP Shopping Service',
    description: data.description || '',
    endpoint: data.apiEndpoint || 'https://api.example.com/mcp',
    categories: selectedCategoryNames,
    bulkSettings: {
      maxQuantity: data.maxBulkQuantity || 100,
      discount: data.bulkDiscount || 0
    }
  }, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(configJson);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Share Configuration
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Share the following configuration with buyers, they can configure and use it in AI Agent tools like Claude Desktop, Dify, n8n, etc.
      </Alert>

      <Typography variant="subtitle2" gutterBottom>
        Configuration Information
      </Typography>
      <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
        <pre style={{ margin: 0, overflow: 'auto', fontSize: '0.875rem' }}>
          {configJson}
        </pre>
      </Paper>
      <Button
        variant="outlined"
        startIcon={<ContentCopy />}
        onClick={handleCopy}
        sx={{ mb: 3 }}
      >
        Copy Configuration
      </Button>

      <Typography variant="subtitle2" gutterBottom>
        Usage Instructions
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary="1. Copy the above configuration to AI Agent tool"
            secondary="Paste this configuration in Claude Desktop, Dify, or n8n"
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="2. AI can access selected product categories"
            secondary={`Including: ${selectedCategoryNames.join(', ')}`}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="3. Supports bulk purchasing and smart filtering"
            secondary="Buyers can upload lists for AI to complete bulk purchases"
          />
        </ListItem>
      </List>
    </Box>
  );
};

const Step5Review = ({ data }) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review & Create
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="textSecondary">Service Name</Typography>
        <Typography variant="body1" gutterBottom>{data.name || 'Not set'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Service Description</Typography>
        <Typography variant="body1" gutterBottom>{data.description || 'Not set'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>API Endpoint</Typography>
        <Typography variant="body1" gutterBottom>{data.apiEndpoint || 'Not set'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Maximum Bulk Quantity</Typography>
        <Typography variant="body1" gutterBottom>{data.maxBulkQuantity || 'Not set'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Bulk Discount Rate</Typography>
        <Typography variant="body1" gutterBottom>{data.bulkDiscount || '0'}%</Typography>
      </Box>
    </Box>
  );
};

const MCPWizard = () => {
  const { addSalesChannel } = useAppContext();
  const navigate = useNavigate();

  const steps = [
    { label: 'Basic Information', component: <Step1BasicInfo /> },
    { label: 'Select Products', component: <Step2Products /> },
    { label: 'Bulk Configuration', component: <Step3BulkSettings /> },
    { label: 'Generate Configuration', component: <Step4ShareConfig /> },
    { label: 'Review & Create', component: <Step5Review /> }
  ];

  const handleComplete = (formData) => {
    const channel = {
      name: formData.name,
      type: 'mcp',
      typeName: 'Universal MCP',
      config: formData
    };
    addSalesChannel(channel);
    navigate('/sales-channels');
  };

  return <StepWizard steps={steps} onComplete={handleComplete} title="Create Universal MCP Service" />;
};

export default MCPWizard;
